import { FDRStatus, ProgramModel, generatePath } from '@cibo/core'
import { usePaginatedFields } from '@cibo/landmanager'
import { Loading, useAuth } from '@cibo/profile'
import {
  EnrollmentWindowClosedModal,
  ProgramIcon,
  useProgram,
  useProgramContent,
  useWorkflows,
} from '@cibo/programs'
import { Markdown } from '@cibo/ui'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { LayoutSection } from 'components/ImpactLayout'
import { ProgramStats } from 'components/ProgramStats'
import { SecondaryNavigation } from 'components/SecondaryNavigation'
import { PROGRAM_ROUTES } from 'navigation/types/routes'
import { useUnprofiledFieldsCount } from 'pages/ProfileFields'
import { ProgramOpportunity } from 'pages/ProgramOpportunity'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router-dom'
import { ProgramAbout } from '../../ProgramAbout'
import { PageLinks, collatePageLinks } from '../utils'
import { eligibleFieldsBaseQuery } from './EligibleFields'
import { EnrollmentWindowAlert } from './EnrollmentWindowAlert'

export const ProgramV1Detail = () => {
  const { t } = useTranslation('impact/pages/ProgramDetailLayout')
  const { persona } = useAuth()
  const { programId } = useParams()
  const content = useProgramContent(programId)

  const program = useProgram({ programId })
  const enrollments = useWorkflows(0, 1, { benchmark: ['enrollment'], programId })
  const baseQuery = useMemo(() => (!!programId ? eligibleFieldsBaseQuery(programId) : undefined), [
    programId,
  ])
  const eligible = usePaginatedFields(0, 1, baseQuery, { enabled: !!programId })
  const enrolled = usePaginatedFields(
    0,
    1,
    { fdrStatusAnyOf: [{ status: FDRStatus.enrolled, programId }] },
    { enabled: !!programId }
  )

  const allFields = usePaginatedFields(0, 1)
  const unprofiled = useUnprofiledFieldsCount()
  const [enrollmentClosedModalOpen, setEnrollmentClosedModalOpen] = useState(false)

  const pageLinks = useMemo<PageLinks>(() => {
    switch (persona) {
      case 'admin':
      case 'advisor':
      default:
        return {
          enrolled: {
            label: t('enrolledFields'),
            to: generatePath(PROGRAM_ROUTES.DETAIL, { programId }),
            exact: false,
          },
          enrollments: {
            label: t('enrollments'),
            to: generatePath(PROGRAM_ROUTES.ENROLLMENTS, { programId }),
            exact: false,
          },
          eligible: {
            label: t('eligibleFields'),
            to: generatePath(PROGRAM_ROUTES.FIELDS_ELIGIBLE, { programId }),
            exact: false,
            count: eligible.data?.numAvailable,
            countColor: 'info',
          },
          about: {
            label: t('about'),
            to: generatePath(PROGRAM_ROUTES.ABOUT, { programId }),
          },
        }
    }
  }, [persona, program.data])

  if (!programId) {
    return null
  }

  if (
    !program.isFetched ||
    !enrollments.isFetched ||
    !allFields.isFetched ||
    !eligible.isFetched ||
    !enrolled.isFetched ||
    unprofiled.isLoading
  ) {
    return <Loading />
  }
  const programAsModel = program.data ? new ProgramModel(program.data) : undefined
  const actualPageLinks = collatePageLinks({
    pageLinks,
    enrolled: enrolled.data,
    enrollments: enrollments.data,
    program: programAsModel,
    eligible: eligible.data,
  })

  return (
    <>
      <LayoutSection>
        <Stack spacing={2}>
          <EnrollmentWindowAlert setEnrollmentClosedModalOpen={setEnrollmentClosedModalOpen} />
          <Stack direction="row" alignItems="center" spacing={1}>
            {!!program.data && <ProgramIcon program={program.data} />}
            <Typography variant="h6" color="text.primary">
              {content.data?.content.fields.title}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={6} direction={{ xs: 'column', md: 'row-reverse' }}>
          {!programAsModel?.isFuture && (
            <Box width={{ xs: undefined, md: 350 }}>
              <Stack spacing={3} flex={1}>
                {persona === 'grower' ? (
                  <ProgramOpportunity programId={programId} sx={{ alignSelf: 'stretch' }} />
                ) : (
                  <ProgramStats programId={programId} />
                )}
              </Stack>
            </Box>
          )}
          <Stack spacing={2} flex={1}>
            {!!actualPageLinks.length ? (
              <>
                <SecondaryNavigation
                  basePath={generatePath(PROGRAM_ROUTES.DETAIL, { programId })}
                  pageLinks={actualPageLinks}
                />
                <Outlet />
              </>
            ) : (
              <ProgramAbout />
            )}
          </Stack>
        </Stack>
      </LayoutSection>

      <EnrollmentWindowClosedModal
        modal={false}
        programId={programId}
        open={enrollmentClosedModalOpen}
        onClose={() => setEnrollmentClosedModalOpen(false)}
        onDeleteSuccess={() => setEnrollmentClosedModalOpen(false)}
      >
        <Markdown>
          {t('deleteEnrollmentDescriptionList', {
            programName: program.data?.content.fields.title,
            context: persona,
            count: enrollments.data?.numItems,
          })}
        </Markdown>
      </EnrollmentWindowClosedModal>
    </>
  )
}
